import { useCallback, useEffect, useRef, useState } from "react";
import { matchRecentRateCommand, rememberRateCommand } from "../lib/rateControl";

let youtubeApiPromise;

function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (youtubeApiPromise) return youtubeApiPromise;
  youtubeApiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      resolve(window.YT);
    };
    const existing = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (!existing) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.onerror = () => reject(new Error("YouTube player could not load"));
      document.head.appendChild(script);
    }
  });
  return youtubeApiPromise;
}

const PLAYER_ERRORS = {
  2: "This YouTube URL is invalid.",
  5: "This video cannot play in the HTML5 player.",
  100: "This video is private, removed, or unavailable.",
  101: "The creator has disabled embedded playback.",
  150: "The creator has disabled embedded playback.",
  153: "YouTube could not verify this player's origin.",
};

export function useYouTubePlayer(videoId, { onRateChange, onDuration, onError } = {}) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const callbacksRef = useRef({ onRateChange, onDuration, onError });
  const rateCommandsRef = useRef([]);
  const snapshotTimerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [availableRates, setAvailableRates] = useState([1]);
  const [error, setError] = useState("");

  callbacksRef.current = { onRateChange, onDuration, onError };

  const readPlayerMetadata = useCallback((player) => {
    const nextDuration = Number(player?.getDuration?.()) || 0;
    const rates = player?.getAvailablePlaybackRates?.() || [1];
    const currentRate = Number(player?.getPlaybackRate?.());
    if (Number.isFinite(currentRate) && currentRate > 0) setPlaybackRate(currentRate);
    if (nextDuration > 0) {
      setDuration(nextDuration);
      callbacksRef.current.onDuration?.(nextDuration);
    }
    if (Array.isArray(rates) && rates.length) {
      setAvailableRates((current) => current.length === rates.length && current.every((rate, index) => rate === rates[index]) ? current : rates);
    }
  }, []);

  const refresh = useCallback(() => {
    const player = playerRef.current;
    if (!player) return null;
    try {
      const time = Number(player.getCurrentTime?.()) || 0;
      const rate = Number(player.getPlaybackRate?.()) || 1;
      setCurrentTime(time);
      setPlaybackRate(rate);
      readPlayerMetadata(player);
      return { currentTime: time, playbackRate: rate };
    } catch {
      // Safari can briefly reject iframe reads while entering or leaving
      // fullscreen. The lifecycle listeners below restart polling afterward.
      return null;
    }
  }, [readPlayerMetadata]);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setPlaybackRate(1);
    setAvailableRates([1]);
    setError("");
    rateCommandsRef.current = [];

    const restartSnapshotTimer = () => {
      window.clearInterval(snapshotTimerRef.current);
      if (!playerRef.current || document.visibilityState === "hidden") return;
      refresh();
      snapshotTimerRef.current = window.setInterval(refresh, 300);
    };
    const resumeSnapshotTimer = () => {
      if (document.visibilityState !== "hidden") restartSnapshotTimer();
    };

    document.addEventListener("fullscreenchange", restartSnapshotTimer);
    document.addEventListener("webkitfullscreenchange", restartSnapshotTimer);
    document.addEventListener("visibilitychange", resumeSnapshotTimer);
    window.addEventListener("pageshow", restartSnapshotTimer);
    window.addEventListener("focus", resumeSnapshotTimer);

    if (!videoId) {
      return () => {
        cancelled = true;
        document.removeEventListener("fullscreenchange", restartSnapshotTimer);
        document.removeEventListener("webkitfullscreenchange", restartSnapshotTimer);
        document.removeEventListener("visibilitychange", resumeSnapshotTimer);
        window.removeEventListener("pageshow", restartSnapshotTimer);
        window.removeEventListener("focus", resumeSnapshotTimer);
      };
    }

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled || !hostRef.current) return;
        const player = new YT.Player(hostRef.current, {
          width: "100%",
          height: "100%",
          videoId,
          playerVars: {
            // TheoFast owns the transport controls and timeline; keep the
            // embedded player's duplicate native control layer hidden.
            controls: 0,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin,
          },
          events: {
            onReady: (event) => {
              if (cancelled) return;
              setReady(true);
              restartSnapshotTimer();
            },
            onStateChange: (event) => {
              if (cancelled) return;
              const isPlaying = event.data === YT.PlayerState.PLAYING;
              setPlaying(isPlaying);
              if (isPlaying || event.data === YT.PlayerState.CUED || event.data === YT.PlayerState.PAUSED) {
                restartSnapshotTimer();
              }
            },
            onPlaybackRateChange: (event) => {
              if (cancelled) return;
              const rate = Number(event.data) || 1;
              setPlaybackRate(rate);
              // The iframe may emit the same programmatic change more than once, or
              // emit it late after the next segment begins. Keep a short history so
              // those events do not masquerade as a manual override.
              const command = matchRecentRateCommand(rateCommandsRef.current, rate);
              callbacksRef.current.onRateChange?.(rate, command?.origin || "user");
            },
            onError: (event) => {
              const message = PLAYER_ERRORS[event.data] || "YouTube could not play this video.";
              setError(message);
              callbacksRef.current.onError?.(message);
            },
          },
        });
        playerRef.current = player;
        restartSnapshotTimer();
      })
      .catch((loadError) => {
        const message = loadError.message || "YouTube player could not load.";
        setError(message);
        callbacksRef.current.onError?.(message);
      });

    return () => {
      cancelled = true;
      window.clearInterval(snapshotTimerRef.current);
      document.removeEventListener("fullscreenchange", restartSnapshotTimer);
      document.removeEventListener("webkitfullscreenchange", restartSnapshotTimer);
      document.removeEventListener("visibilitychange", resumeSnapshotTimer);
      window.removeEventListener("pageshow", restartSnapshotTimer);
      window.removeEventListener("focus", resumeSnapshotTimer);
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, [readPlayerMetadata, refresh, videoId]);

  const setRate = useCallback((rate, origin = "manual") => {
    const player = playerRef.current;
    if (!player?.setPlaybackRate) return false;
    const now = Date.now();
    rateCommandsRef.current = rememberRateCommand(rateCommandsRef.current, { rate, at: now, origin }, now);
    player.setPlaybackRate(rate);
    window.setTimeout(() => {
      if (!playerRef.current?.getPlaybackRate) return;
      const actual = Number(playerRef.current.getPlaybackRate()) || 1;
      setPlaybackRate(actual);
      if (origin === "adaptive" && Math.abs(actual - rate) >= 0.01) {
        callbacksRef.current.onRateChange?.(actual, "unsupported");
      }
    }, 700);
    return true;
  }, []);

  const seekTo = useCallback((seconds) => {
    const player = playerRef.current;
    if (!player?.seekTo || !Number.isFinite(seconds) || seconds < 0) return false;
    player.seekTo(seconds, true);
    setCurrentTime(seconds);
    window.setTimeout(() => refresh(), 180);
    return true;
  }, [refresh]);

  const pause = useCallback(() => {
    if (!playerRef.current?.pauseVideo) return false;
    playerRef.current.pauseVideo();
    return true;
  }, []);

  const play = useCallback(() => {
    if (!playerRef.current?.playVideo) return false;
    playerRef.current.playVideo();
    return true;
  }, []);

  return {
    hostRef,
    ready,
    playing,
    currentTime,
    duration,
    playbackRate,
    availableRates,
    error,
    setRate,
    refresh,
    seekTo,
    pause,
    play,
  };
}
